import React from 'react'

import { Option } from 'components/elements/ArchidektDropdown/types'

import BookmarkService from 'services/bookmark.service'
import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'

import { store } from 'redux/store'
import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { toggleModal as toggleModalAction } from 'redux/active/actions'
import { selectOwnsDeck, useCanEditDeck } from 'redux/deck/selectors'

import ContextMenu from 'components/elements/ContextMenu'

import styles from './contextMenu.module.scss'

type Props = {
  children: React.ReactNode
  className?: string
}

const DeckPageContextMenu = ({ children, className }: Props) => {
  const [setActiveState, setDeckState] = useActionless(SET_ACTIVE_STATE, SET_DECK_STATE)
  const [toggleModal] = useActions(toggleModalAction)

  const deckId = useAppSelector(state => state.deck.id)
  const ownsDeck = useAppSelector(selectOwnsDeck)
  const canEditDeck = useCanEditDeck()
  const undoStack = useAppSelector(state => state.deck.actionStack)
  const redoStack = useAppSelector(state => state.deck.undoneActionStack)
  const parentFolder = useAppSelector(state => state.deck.parentFolder)
  const bookmarked = useAppSelector(state => state.deck.bookmarked)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)

  const handleToggleBookmark = () => {
    if (bookmarked)
      return BookmarkService.remove(deckId)
        .then(() => ToastService.create('Bookmark removed', 'Bookmark service', 'success'))
        .then(() => setDeckState({ bookmarked: false }))
        .catch(() => ToastService.create('Unable to delete bookmark', 'Bookmark service'))

    return BookmarkService.create(deckId)
      .then(() => ToastService.create('Deck bookmarked', 'Bookmark service', 'success'))
      .then(() => setDeckState({ bookmarked: true }))
      .catch(() => ToastService.create('Unable to create bookmark', 'Bookmark service'))
  }

  const options: Option[] = [
    {
      label: 'Open search',
      icon: 'search',
      onClick: () => setActiveState({ searchOpen: 'search' }),
      keybind: 'Ctrl + Enter',
    },
    { type: 'spacer' },
    {
      label: 'Undo',
      icon: 'undo',
      onClick: () => SaveService.undo(),
      keybind: 'Ctrl + Z',
      disabled: undoStack.length === 0,
      hidden: !canEditDeck,
    },
    {
      label: 'Redo',
      icon: 'redo',
      onClick: () => SaveService.redo(),
      keybind: 'Ctrl + Shift + Z',
      disabled: redoStack.length === 0,
      hidden: !canEditDeck,
    },
    {
      label: 'Deck history',
      type: 'extras',
      id: 'history',
      icon: 'history',
      options: [
        {
          label: 'Recent deck changes',
          onClick: () => toggleModal('recentChanges', true),
        },
        {
          label: 'Added / removed cards',
          onClick: () => toggleModal('addRemovedCards', true),
        },
        {
          label: 'Deck snapshots',
          onClick: () => toggleModal('snapshots', true),
        },
        { type: 'spacer', hidden: !canEditDeck },
        {
          icon: 'add',
          label: 'Create deck snapshot',
          onClick: () => toggleModal('createSnapshotStandalone', true),
          hidden: !canEditDeck,
        },
      ],
    },
    { type: 'spacer' },
    {
      type: 'extras',
      label: 'Card actions',
      id: 'extra-card-actions',
      icon: 'pencil',
      options: [
        {
          label: 'Import cards',
          icon: 'paste',
          onClick: () => toggleModal('massEditModal', true),
          hidden: !canEditDeck,
        },
        {
          label: 'Export cards',
          icon: 'external',
          onClick: () => toggleModal('export', true),
        },
        {
          label: 'Add deck to collection',
          icon: 'add circle',
          onClick: () => toggleModal('addToCollection', true),
        },
        {
          label: 'Copy Deck',
          icon: 'copy',
          onClick: () => toggleModal('cloneModal', true),
        },
      ],
    },
    {
      type: 'extras',
      id: 'more-options-extras',
      label: 'More options',
      icon: 'arrow right',
      options: [
        {
          label: 'Select all',
          keybind: 'Ctrl + A',
          onClick: () => {
            const { cardMap } = store.getState().deck
            const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

            setActiveState({ multiSelectedIds })
          },
          icon: 'hand pointer outline',
        },
        {
          label: 'Optimize printings',
          onClick: () => {
            const { cardMap } = store.getState().deck
            const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

            setActiveState({ multiSelectedIds, optimizeCardOverlay: true })
          },
          icon: 'money bill alternate outline',
        },
        {
          label: 'Basic land optimizer',
          id: 'lands',
          icon: 'fire',
          onClick: () => setActiveState({ autofillBasicLandsOverlay: true }),
        },
        {
          label: 'Generate deck image',
          to: `/decks/image/${deckId}/`,
          icon: 'image outline',
          hidden: !deckId, // I'm just gonna disable the button for sandbox since I don't feel like fixing it. It mostly works if we wanted to implement it, but there's no deck name
        },
        {
          label: 'Open containing folder',
          to: `/folders/${parentFolder}`,
          icon: 'folder open',
          hidden: !ownsDeck,
        },
        {
          label: 'Bookmark deck',
          icon: bookmarked ? 'bookmark' : 'bookmark outline',
          onClick: handleToggleBookmark,
          className: bookmarked ? styles.bookmarked : '',
        },
      ],
    },
    {
      label: 'Deck Settings',
      icon: 'cog',
      onClick: () => toggleModal('deckSettings', true),
      hidden: !canEditDeck,
    },
    { type: 'message', label: 'Ctrl + Right Click for standard menu' },
  ]

  const multiEditOptions: Option[] = [
    {
      icon: 'money bill alternate outline',
      label: 'Optimize selected cards',
      onClick: () => setActiveState({ optimizeCardOverlay: true }),
    },
    {
      icon: 'cancel',
      label: 'Unselect all cards',
      onClick: () => setActiveState({ multiSelectedIds: {} }),
    },
    { type: 'message', label: 'Left click to continue multi-selecting' },
  ]

  return (
    <ContextMenu
      className={className}
      menuClassName={styles.menu}
      options={!Object.keys(multiSelectedIds).length ? options : multiEditOptions}>
      {children}
    </ContextMenu>
  )
}

export default DeckPageContextMenu
